import { FC, useRef, useState } from 'react'
import classNames from 'classnames'
import { ReactComponent as ChevronIcon } from 'src/assets/svg/chevron-current.svg'
import useOutsideClick from 'src/hooks/useOutsideClick'
import styles from './headerDropdown.module.scss'

export interface HeaderDropdownOption {
  key: string
  label: string
}

interface HeaderDropdownProps {
  // Default label shown when the 'all' option is selected (e.g. "Source").
  defaultLabel: string
  icon: FC<React.SVGProps<SVGSVGElement>>
  onChange: (key: string) => void
  options: HeaderDropdownOption[]
  // Selected option key; 'all' shows the default label as inactive.
  value: string
}

// Compact pill dropdown used in the Smart Insights header (Source / Project).
// Matches the category-tab styling: bordered pill, purple accent when a
// concrete option is selected.
export const HeaderDropdown: FC<HeaderDropdownProps> = ({
  defaultLabel,
  icon: Icon,
  onChange,
  options,
  value,
}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useOutsideClick({ onOutsideClick: () => setOpen(false), ref })

  const selected = options.find((o) => o.key === value && o.key !== 'all')
  const isActive = !!selected

  const handleSelect = (key: string) => {
    onChange(key)
    setOpen(false)
  }

  return (
    <div className={styles.wrapper} ref={ref}>
      <button
        className={classNames(styles.trigger, {
          [styles.triggerActive]: isActive,
        })}
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <Icon className={styles.triggerIcon} />
        <span className={styles.triggerLabel}>
          {selected ? selected.label : defaultLabel}
        </span>
        <ChevronIcon
          className={classNames(styles.chevron, { [styles.chevronOpen]: open })}
        />
      </button>

      {open && (
        <div className={styles.menu}>
          {options.map((option) => (
            <button
              className={classNames(styles.menuItem, {
                [styles.menuItemActive]: option.key === value,
              })}
              key={option.key}
              onClick={() => handleSelect(option.key)}
              type="button"
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
